/**
 * Button Component
 *
 * A strictly-typed button component following the Tangi Design System.
 * **ENFORCES** design tokens and **PREVENTS** styling bypasses.
 *
 * Features:
 * - Strict variants: primary, secondary, outline, ghost, destructive, link
 * - Strict sizes: sm, md, lg, icon
 * - Loading state with accessible spinner
 * - Focus ring using design tokens
 * - Mobile-first touch targets (min 44px)
 * - Full accessibility support
 * - **className filtering**: Only layout classes allowed (margins, positioning)
 *
 * @example
 * ```tsx
 * // ✅ CORRECT - Use variant props
 * <Button variant="primary" size="lg" onClick={handleSubmit}>
 *   Create Magic
 * </Button>
 *
 * // ✅ CORRECT - Layout className allowed
 * <Button variant="outline" className="mt-4">
 *   Save
 * </Button>
 *
 * // ❌ BLOCKED - Styling className filtered out
 * <Button className="bg-red-500 rounded-full">
 *   This won't override styling
 * </Button>
 * ```
 *
 * @see docs/tokens.md for token documentation
 * @see types/button.types.ts for type definitions
 */

import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cn} from '@/shared/utils/utils';
import {LoadingSpinner} from './LoadingSpinner';
import type {ButtonProps} from './types/button.types';
import {buttonVariants} from './Button.variants';
import {filterLayoutClasses} from './Button.utils';

// ============================================================================
// BUTTON COMPONENT
// ============================================================================

/**
 * Button component with strict design system enforcement.
 *
 * **Design Principles:**
 * - className is FILTERED to only allow layout classes
 * - Styling classes are BLOCKED and logged in development
 * - Use variant/size props for visual changes
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      children,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      disabled = false,
      fullWidth = false,
      type = 'button',
      onClick,
      className,
      title,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'aria-pressed': ariaPressed,
      'aria-expanded': ariaExpanded,
      'aria-controls': ariaControls,
      'aria-haspopup': ariaHasPopup,
      'data-testid': testId,
      tabIndex,
      name,
      value,
      form,
      autoFocus,
      onKeyDown,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      onPointerDown,
      onPointerUp,
      asChild = false,
    },
    ref
  ): React.ReactElement {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || isLoading;

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>): void => {
        if (isLoading) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      },
      [isLoading, onClick]
    );

    // ENFORCE: Filter className to only allow layout classes
    const filteredClassName = filterLayoutClasses(className);

    const buttonClassName = cn(
      buttonVariants({ variant, size, fullWidth, isLoading }),
      asChild && isDisabled && 'pointer-events-none opacity-50',
      filteredClassName
    );

    return (
      <Comp
        ref={ref}
        {...(!asChild && {
          type,
          disabled: isDisabled,
          name,
          value,
          form,
          autoFocus,
        })}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        tabIndex={tabIndex}
        className={buttonClassName}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-pressed={ariaPressed}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        aria-haspopup={ariaHasPopup}
        aria-disabled={isDisabled || undefined}
        aria-busy={isLoading || undefined}
        data-testid={testId}
        data-loading={isLoading || undefined}
        data-variant={variant}
        data-size={size}
        title={title}
      >
        {asChild ? (
          children
        ) : (
          <>
            {isLoading && <LoadingSpinner size={size} aria-label="Loading, please wait" />}
            {isLoading && <span className="sr-only">Loading, please wait</span>}
            <span className={cn('inline-flex items-center gap-2', isLoading && 'opacity-70')}>
              {children}
            </span>
          </>
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

// ============================================================================
// EXPORTS
// ============================================================================

export { Button };
;
